import type { FormEvent } from "react";
import type { FamilySummary } from "@/services/screen-budget/contracts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface FamilyManagementCardProps {
  families: FamilySummary[];
  selectedFamilyId: number | null;
  onSelectFamily: (familyId: number) => void;
  createFamilyName: string;
  onCreateFamilyNameChange: (value: string) => void;
  onCreateFamily: () => Promise<void>;
  isCreatingFamily: boolean;
  deleteTargetFamilyId: number | null;
  onToggleDeleteFamily: (familyId: number) => void;
  deleteConfirmName: string;
  onDeleteConfirmNameChange: (value: string) => void;
  deleteConfirmCascade: boolean;
  onDeleteConfirmCascadeChange: (value: boolean) => void;
  onConfirmDeleteFamily: () => Promise<void>;
  isDeleteMutationPending: boolean;
}

export function FamilyManagementCard({
  families,
  selectedFamilyId,
  onSelectFamily,
  createFamilyName,
  onCreateFamilyNameChange,
  onCreateFamily,
  isCreatingFamily,
  deleteTargetFamilyId,
  onToggleDeleteFamily,
  deleteConfirmName,
  onDeleteConfirmNameChange,
  deleteConfirmCascade,
  onDeleteConfirmCascadeChange,
  onConfirmDeleteFamily,
  isDeleteMutationPending,
}: FamilyManagementCardProps) {
  const handleCreateFamily = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onCreateFamily();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Family Management</CardTitle>
        <CardDescription>Create families and delete families you own.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form onSubmit={handleCreateFamily} className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="grid gap-2">
            <Label htmlFor="screen-budget-admin-create-family">Family name</Label>
            <Input
              id="screen-budget-admin-create-family"
              value={createFamilyName}
              onChange={event => onCreateFamilyNameChange(event.currentTarget.value)}
              placeholder="My Family"
            />
          </div>
          <Button type="submit" className="self-end" disabled={isCreatingFamily}>
            {isCreatingFamily ? "Creating..." : "Create family"}
          </Button>
        </form>

        {families.length === 0 ? (
          <p className="text-sm text-muted-foreground">No families are available for your account yet.</p>
        ) : (
          <div className="grid gap-2">
            {families.map(family => {
              const isSelected = family.id === selectedFamilyId;
              const isDeleteExpanded = family.id === deleteTargetFamilyId;
              const canDelete = family.role === "owner";
              const canConfirmDelete = deleteConfirmCascade && deleteConfirmName === family.name;

              return (
                <div key={family.id} className="rounded-md border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{family.name}</p>
                      <p className="text-xs text-muted-foreground">Role: {family.role}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" variant={isSelected ? "default" : "outline"} onClick={() => onSelectFamily(family.id)}>
                        {isSelected ? "Selected" : "Select"}
                      </Button>
                      {canDelete ? (
                        <Button type="button" size="sm" variant="outline" onClick={() => onToggleDeleteFamily(family.id)}>
                          {isDeleteExpanded ? "Cancel delete" : "Delete family"}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {isDeleteExpanded ? (
                    <div className="mt-3 grid gap-3 rounded-md border border-red-200 bg-red-50 p-3">
                      <p className="text-sm font-medium text-red-700">Delete family (irreversible)</p>
                      <p className="text-xs text-red-700">
                        This will permanently delete the family and all related kids, messages, pairing codes, and tokens.
                      </p>
                      <div className="grid gap-2">
                        <Label htmlFor={`delete-family-name-${family.id}`} className="text-xs">
                          Type the family name to confirm
                        </Label>
                        <Input
                          id={`delete-family-name-${family.id}`}
                          value={deleteConfirmName}
                          onChange={event => onDeleteConfirmNameChange(event.currentTarget.value)}
                          placeholder={family.name}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={deleteConfirmCascade}
                          onChange={event => onDeleteConfirmCascadeChange(event.currentTarget.checked)}
                        />
                        I understand this deletion cascades to all related Screen Budget data.
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        className="justify-self-start border-red-300 text-red-700 hover:bg-red-100"
                        disabled={!canConfirmDelete || isDeleteMutationPending}
                        onClick={() => void onConfirmDeleteFamily()}
                      >
                        {isDeleteMutationPending ? "Deleting..." : "Confirm delete family"}
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
